import { Injectable } from "@angular/core";

import { TransactionDirection } from "../../../models/enum/transactionDirection";
import { TransactionLinkType } from "../../../models/types/transaction.types";
import { TransactionView } from "../../../models/view/transaction/transaction.view";

/**
 * Default auto-detection window, used when no configured value is passed to
 * {@link TransactionConversionService.findRelatedTransaction}. The live value
 * comes from the Transfers & Conversions plugin settings
 * (`PluginConfigService.getTransferConversionOptions().linkWindowHours`) and is
 * threaded in by the enrichment collection - kept out of this service so it
 * stays free of DI wiring and safe to `new` in tests.
 */
export const DEFAULT_LINK_WINDOW_MS = 24 * 60 * 60 * 1000; // 24 hours

@Injectable({
  providedIn: "root",
})
export class TransactionConversionService {
  /**
   * Classifies transactions that were previously linked via the estimate-transfer
   * flow (persisted `linkedTo`). Sets `c_linkType` so the UI can render a badge
   * without re-deriving the type from IDs.
   *
   * Also clears stale `c_linkType` when a persisted link has been removed but
   * the enrichment clone still carries the value from the previous cycle. This
   * ensures `findRelatedTransaction` sees a clean slate for re-classification.
   *
   * Must be called before `findRelatedTransaction`.
   */
  classifyExistingLinks(transactions: TransactionView[]): void {
    if (!transactions?.length) {
      return;
    }

    const byId = new Map(transactions.map((t) => [t.id, t]));

    for (const transaction of transactions) {
      if (!transaction.linkedTo?.length) {
        if (!transaction.c_linkedTo.length && transaction.c_linkType !== null) {
          transaction.c_linkType = null;
        }
        continue;
      }
      if (transaction.c_linkType !== null) {
        continue;
      }

      const partnerId = transaction.linkedTo[0];
      const partner = byId.get(partnerId);
      if (!partner) {
        continue;
      }

      const sameSymbol =
        transaction.quantity.actualQuantity.symbol === partner.quantity.actualQuantity.symbol;
      const linkType: TransactionLinkType = sameSymbol ? "transfer" : "conversion";

      transaction.c_linkType = linkType;
      if (partner.c_linkType === null) {
        partner.c_linkType = linkType;
      }
    }
  }

  /**
   * Auto-detects related transactions (transfers and conversions) and populates
   * the computed `c_linkedTo` / `c_linkType` fields. Transactions are matched
   * greedily and one-to-one: once a transaction is linked it is not considered
   * again.
   *
   * The results are recomputed on every enrichment cycle and never persisted.
   * Deliberate links (`linkedTo`, e.g. from the estimate-transfer flow) are
   * respected and left untouched, and pairs the user has explicitly unlinked
   * (`excludedLinkIds`) are never re-linked.
   *
   * Must be called with mutable views (enrichment clones) - store views are
   * frozen and cannot be written to.
   */
  findRelatedTransaction(transactions: TransactionView[], windowMs?: number): void {
    if (!transactions || transactions.length === 0) {
      return;
    }

    /** Non-positive or missing config falls back to the default window. */
    const effectiveWindowMs = windowMs && windowMs > 0 ? windowMs : DEFAULT_LINK_WINDOW_MS;

    /** Sort a copy by postDate so candidates within the window are adjacent,
        while mutating the passed-in view instances in place. Views without a
        postDate (e.g. built outside the store-model constructor) cannot be
        date-matched and are excluded. */
    const sorted = transactions
      .filter((transaction) => transaction.postDate instanceof Date)
      .sort((a, b) => a.postDate.getTime() - b.postDate.getTime());

    for (let i = 0; i < sorted.length; i++) {
      const currentTrans = sorted[i];

      if (this.isAlreadyLinked(currentTrans)) {
        continue;
      }

      for (let j = i + 1; j < sorted.length; j++) {
        const nextTrans = sorted[j];

        /** Sorted ascending: once we pass the window, no later transaction can match. */
        if (
          !this.isWithinLinkWindow(currentTrans.postDate, nextTrans.postDate, effectiveWindowMs)
        ) {
          break;
        }

        if (this.isAlreadyLinked(nextTrans) || this.isExcludedPair(currentTrans, nextTrans)) {
          continue;
        }

        if (this.linkRelatedTransactions(currentTrans, nextTrans)) {
          break;
        }
      }
    }
  }

  /**
   * Links a pair if they form a transfer or conversion. Returns whether a link
   * was made.
   *
   * Transfer: same symbol, different accounts, opposite directions, equal amounts.
   * Conversion: different symbols, same account, opposite directions.
   *
   * Requiring opposite directions prevents two unrelated same-direction
   * transactions (e.g. two deposits) from being falsely linked.
   * For transfers, equal amounts is an additional guard against coincidental
   * same-symbol activity across accounts on the same day.
   */
  private linkRelatedTransactions(
    currentTrans: TransactionView,
    nextTrans: TransactionView,
  ): boolean {
    const linkType = this.linkTypeFor(currentTrans, nextTrans);

    if (linkType) {
      currentTrans.c_linkedTo.push(nextTrans.id);
      nextTrans.c_linkedTo.push(currentTrans.id);
      currentTrans.c_linkType = nextTrans.c_linkType = linkType;
      return true;
    }

    return false;
  }

  /**
   * Returns the link type a pair forms, or null if they are not linkable.
   *
   * Transfer: same symbol, different accounts, opposite directions, equal amounts.
   * Conversion: different symbols, same account, opposite directions.
   *
   * Single source of truth for the linking *conditions* (symbol, account,
   * direction, amount), shared by auto-detection (`linkRelatedTransactions`) and
   * the edit-time re-check (`isStillLinkable`) so the two never drift apart.
   * Auto-detection layers extra candidate gates on top of these conditions — the
   * 24h search window (`isWithinLinkWindow`) and the user's unlink veto
   * (`isExcludedPair`) — which are deliberately not part of `linkTypeFor`.
   */
  private linkTypeFor(a: TransactionView, b: TransactionView): TransactionLinkType | null {
    const sameAccount = this.isSameBankAccount(a.accountLink.id, b.accountLink.id);
    const sameSymbol = a.quantity.actualQuantity.symbol === b.quantity.actualQuantity.symbol;
    const oppositeDirections = this.hasOppositeDirections(a, b);

    if (!sameAccount && sameSymbol && oppositeDirections && this.hasEqualAmounts(a, b)) {
      return "transfer";
    }

    if (sameAccount && !sameSymbol && oppositeDirections) {
      return "conversion";
    }

    return null;
  }

  /**
   * Whether two transactions still satisfy the transfer/conversion linking
   * conditions. Used to auto-unlink a persisted pair after one side is edited so
   * the link no longer holds (issue #293). Re-checks only the conditions in
   * `linkTypeFor` — deliberately not the 24h search window or the unlink veto that
   * auto-detection also applies: an edit (e.g. to the date) shouldn't re-break a
   * link over the candidate-search bound, and a broken condition is not a user veto.
   */
  isStillLinkable(a: TransactionView, b: TransactionView): boolean {
    return this.linkTypeFor(a, b) !== null;
  }

  /**
   * The link type a manually-chosen pair would form, or null if they are not a
   * valid transfer/conversion. Public accessor for the manual link flow
   * ({@link TransactionLinkService.linkPair}) so a user-initiated link is gated
   * on the same predicate as auto-detection and the two can never drift. Unlike
   * auto-detection this does not consider the time window - a deliberate link
   * may join transactions further apart.
   */
  getLinkType(a: TransactionView, b: TransactionView): TransactionLinkType | null {
    return this.linkTypeFor(a, b);
  }

  /**
   * A transaction carrying either a deliberate persisted link (`linkedTo`) or
   * an auto-detected link from this cycle (`c_linkedTo`) is not linked again.
   */
  private isAlreadyLinked(transaction: TransactionView): boolean {
    return transaction.linkedTo?.length > 0 || transaction.c_linkedTo.length > 0;
  }

  /** The user has explicitly unlinked this pair - never re-link it. */
  private isExcludedPair(a: TransactionView, b: TransactionView): boolean {
    return a.excludedLinkIds.includes(b.id) || b.excludedLinkIds.includes(a.id);
  }

  private isSameBankAccount(currentAcct: string, nextAcct: string) {
    return currentAcct === nextAcct;
  }

  private hasOppositeDirections(a: TransactionView, b: TransactionView) {
    return (
      (a.direction === TransactionDirection.In && b.direction === TransactionDirection.Out) ||
      (a.direction === TransactionDirection.Out && b.direction === TransactionDirection.In)
    );
  }

  private hasEqualAmounts(a: TransactionView, b: TransactionView) {
    return a.quantity.actualQuantity.amount === b.quantity.actualQuantity.amount;
  }

  private isWithinLinkWindow(currentDate: Date, nextDate: Date, windowMs: number) {
    return Math.abs(currentDate.getTime() - nextDate.getTime()) <= windowMs;
  }
}
